<script setup lang="ts">
import { CtButton } from '@camelot/components'
import { storeToRefs } from 'pinia'
import { formatAmount, getOfferDaysDuration } from '~/composables/offer-utils'
import { translate, useTranslations } from '~/composables/translate/translations'
import { usePrebookingButtonLogic } from '~/composables/usePrebookingButtonLogic'
import { useDateTimeStore } from '~/stores/datetime'
import { useExtrasStore } from '~/stores/extras'
import { useOfferStore } from '~/stores/offer'
import { useReferralStore } from '~/stores/referral'
import { useStepStore } from '~/stores/step'
import { STEPS_RECORD } from '~/stores/steps-definition'

const referralStore = useReferralStore()
const metaFlowOffersEnabled = useRuntimeConfig().public.META_OFFERS_ENABLED && referralStore.isMetaFlow

const stepStore = useStepStore()
const { noFeeCancellationDate } = storeToRefs(useDateTimeStore())
const { selectedOffer, getOfferDescription } = storeToRefs(useOfferStore())
const { totalExtraPrice } = storeToRefs(useExtrasStore())

const {
  isLoading,
  onButtonPress,
  buttonText,
  isPaymentPage,
  errorPrebookingCreation,
  paymentSetupStore: paymentSetupStoreFromComposable,
} = usePrebookingButtonLogic()

const paymentSetupStore = paymentSetupStoreFromComposable

const isFinalStep = computed<boolean>(() => STEPS_RECORD[stepStore.currentStepPath!]?.path === 'personal')
const buttonTextSize = computed<string>(() => isFinalStep.value ? 'text-sm' : '')

const dateWithFreeCancellation = computed<string>(() => {
  if (!noFeeCancellationDate.value)
    return '???'
  return noFeeCancellationDate.value.toLocaleDateString(useTranslations().language.value, { day: '2-digit', month: '2-digit' })
})

const lastHourToCancel = computed<string | undefined>(() => {
  if (!noFeeCancellationDate.value)
    return '???'
  return noFeeCancellationDate.value.toLocaleTimeString(useTranslations().language.value, { hour12: false, hour: '2-digit', minute: '2-digit' })
})

const totalTextFormatted = computed<string>(() => {
  const duration: number = getOfferDaysDuration()!
  return (duration === 1 ? translate('sidebar_for_day') : translate('sidebar_for_days')).replace('%', String(duration))
})

const totalPrice = computed<string | undefined>(() =>
  formatAmount(useTranslations().language.value, (selectedOffer.value?.price ?? 0) + totalExtraPrice.value, useOfferStore().selectedOffer?.currency),
)

const packageInclusiveDistance = computed(() =>
  selectedOffer.value && (selectedOffer.value.properties || []).find(prop => (prop.tags || []).includes('package_inclusive_distance')),
)
const distancePrice = computed(() =>
  selectedOffer.value && (selectedOffer.value.properties || []).find(prop => (prop.tags || []).includes('distance_price')),
)
</script>

<template>
  <section
    class="px-16 pt-24 pb-24
           flex flex-col
           bg-white rounded-t-2xl
           border-b-8 border-b-cotton
           top-[80px] sticky"
  >
    <h3 class="mb-16 text-black-100">
      {{ translate('sidebar_my_booking_title') }}
    </h3>

    <header class="flex justify-between">
      <div class="ml-8 basis-7/12 text-black-80 text-xs leading-normal">
        <strong>{{ translate('prebook_total_description') }}</strong> {{ totalTextFormatted }}
      </div>
      <div class="text-right text-black-100">
        <Transition mode="out-in" name="fade-bounce">
          <p :key="totalPrice">
            <strong>{{ totalPrice }}</strong>
          </p>
        </Transition>
        <p class="text-black-80 text-xs leading-normal">
          <template v-if="selectedOffer?.mileageIncluded">
            {{ translate('mileage_included') }}
          </template>
          <template v-else-if="metaFlowOffersEnabled && packageInclusiveDistance?.value">
            {{
              translate('sidebar_included_kilometers', {
                '{package_inclusive_distance}': packageInclusiveDistance.translatedValue || packageInclusiveDistance.value,
              })
            }}
          </template>
          <template v-else>
            {{ useOfferStore().getFormattedPricePerDay }}/{{ translate('amount_per_day') }}
          </template>
        </p>
        <p class="text-black-80 text-xs leading-normal">
          <template v-if="metaFlowOffersEnabled && distancePrice?.value && !selectedOffer?.mileageIncluded">
            {{
              translate('sidebar_distance_price', {
                '{distance_price}': distancePrice.value,
              })
            }}
          </template>
          <template v-else-if="!selectedOffer?.mileageIncluded">
            {{ getOfferDescription }}
          </template>
        </p>
      </div>
    </header>

    <p v-if="noFeeCancellationDate" class="text-center mt-28 font-bold text-black-60 text-xs leading-normal">
      {{ translate('sidebar_free_cancellation') }} {{ dateWithFreeCancellation }} I {{ lastHourToCancel }}
    </p>

    <CtButton
      class="mt-8 text-center" data-testid="sidebar-button" :loading="isLoading"
      :disabled="isLoading || errorPrebookingCreation !== undefined || (isPaymentPage && !paymentSetupStore.isStripeReady)" @click="onButtonPress()"
    >
      <span :class="buttonTextSize">{{ buttonText }}</span>
    </CtButton>
  </section>
</template>

<style scoped lang="scss">
.fade-bounce-leave-active {
  animation: bounce 0.3s, scale-opacity 0.3s;
}

.fade-bounce-enter-active {
  animation: bounce 0.1s, scale-opacity 0.1s;
}

.fade-bounce-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

@keyframes bounce {

  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }

  40% {
    transform: translateY(-25px);
  }

  60% {
    transform: translateY(-10px);
  }
}

@keyframes scale-opacity {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  60% {
    opacity: 0.3;
    transform: scale(1.1);
  }
}
</style>
